// /src/components/core/HeadersAndFooters/FooterContainer.jsx
// Base footer container - 120px height, two-section layout
// Includes universal Help button (50x50px, 40px from right edge)

import { useState, useEffect, useLayoutEffect } from 'react';
import StandardButton from '../StandardButton';
import ErrorReportModal from '../ErrorReportModal';
import { useTranslation } from '../../../i18n/useTranslation';

// Height-responsive branding (logo + tagline) - scales smoothly between these two viewport
// heights, clamped outside the range. Deliberately local to this component rather than added
// to the shared useMediaQuery hook: that hook is imported by 33+ files (including every
// StandardButton instance), and its width-tracking effect carries a documented same-value
// setState guard specifically because unguarded resize events caused an app-wide render loop
// (Phase 5a Addendum 2, see useMediaQuery.js). A second, height-tracking state var there would
// re-render everything on every height-changing resize (e.g. mobile browser chrome show/hide)
// for a need only this branding block has. See
// docs/briefs/2026-09-05-article-detail-left-sidebar-rearrange-and-responsive-footer.md
const BRANDING_HEIGHT_MIN = 600;  // viewport height floor (brief's design floor)
const BRANDING_HEIGHT_MAX = 900;  // viewport height at/above which branding is full size
const BRANDING_LOGO_MIN = { width: 44, height: 43 };
const BRANDING_LOGO_MAX = { width: 80, height: 78 };
const BRANDING_TAGLINE_MIN_PX = 10;
const BRANDING_TAGLINE_MAX_PX = 14;

const calculateBrandingScaleRatio = (viewportHeight) => {
  const clamped = Math.min(Math.max(viewportHeight, BRANDING_HEIGHT_MIN), BRANDING_HEIGHT_MAX);
  return (clamped - BRANDING_HEIGHT_MIN) / (BRANDING_HEIGHT_MAX - BRANDING_HEIGHT_MIN);
};

// `embedded` renders the branding footer in-flow (position:relative, width:100%) instead of
// as a position:fixed; left:0; right:0 bar. The fixed/full-width form ignored any width-limited
// wrapper (fixed = viewport-relative), so on the front page it spilled across the whole window;
// embedded mode lets a width:${sidebarWidth} wrapper actually clip it under the left sidebar.
// Embedded also hides the in-footer Help button — the front page floats its own Help button.
function FooterContainer({ children, viewType = 'feed', currentView, currentContext, isMobile = false, embedded = false }) {
  const [showErrorReport, setShowErrorReport] = useState(false);
  const { t } = useTranslation('ui');

  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 900
  );

  // Mirrors useMediaQuery's own first-paint re-measure + same-value guard (see that file for
  // why the guard matters — an unguarded resize handler re-renders on every native event even
  // when the value hasn't changed).
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    setWindowHeight(prev => (prev === window.innerHeight ? prev : window.innerHeight));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setWindowHeight(prev => (prev === window.innerHeight ? prev : window.innerHeight));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const brandingScaleRatio = calculateBrandingScaleRatio(windowHeight);
  const brandingLogoWidth = Math.round(BRANDING_LOGO_MIN.width + (BRANDING_LOGO_MAX.width - BRANDING_LOGO_MIN.width) * brandingScaleRatio);
  const brandingLogoHeight = Math.round(BRANDING_LOGO_MIN.height + (BRANDING_LOGO_MAX.height - BRANDING_LOGO_MIN.height) * brandingScaleRatio);
  const brandingTaglineSize = Math.round(BRANDING_TAGLINE_MIN_PX + (BRANDING_TAGLINE_MAX_PX - BRANDING_TAGLINE_MIN_PX) * brandingScaleRatio);

  // Derive currentView from viewType or URL if not provided
  const derivedView = currentView || (() => {
    const path = window.location.pathname;
    if (path.includes('/article')) return 'Article Detail';
    if (path.includes('/event')) return 'Event Detail';
    if (path.includes('/chat')) return 'Chat Detail';
    if (path.includes('/group')) return 'Group';
    if (path.includes('/profile')) return 'My Profile';
    if (path.includes('/images')) return 'My Images';
    if (path.includes('/tags')) return 'My Tags';
    if (path.includes('/locations')) return 'My Locations';
    if (path.includes('/articles')) return 'My Articles';
    // Fallback to viewType
    const viewTypeMap = { feed: 'Feed', form: 'Form', detail: 'Detail', list: 'List' };
    return viewTypeMap[viewType] || 'Unknown View';
  })();

  // Derive currentContext if not provided
  const derivedContext = currentContext || {
    name: derivedView,
    type: viewType || 'page',
    component: 'FooterContainer'
  };

  // Mobile footer - simplified, 60px height, no branding
  if (isMobile) {
    return (
      <>
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60px',
          background: '#EDF2F8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          borderTop: '1px solid #E0E0E0'
        }}>
          <div style={{
            width: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 60px' // Leave space for help button
          }}>
            {children}
          </div>

          {/* Help Button - smaller, closer to edge */}
          <div style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)'
          }}>
            <StandardButton
              variant="icon"
              text="?"
              color="#F9A61B"
              size="small"
              onClick={() => setShowErrorReport(true)}
            />
          </div>
        </div>

        {/* Error Report Modal */}
        <ErrorReportModal
          isOpen={showErrorReport}
          onClose={() => setShowErrorReport(false)}
          currentView={derivedView}
          currentContext={derivedContext}
        />
      </>
    );
  }

  // For form views, show simplified footer with just buttons (no branding)
  if (viewType === 'form') {
    return (
      <>
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: isMobile ? '60px' : '120px',
          background: '#EDF2F8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          borderTop: isMobile ? '1px solid #E0E0E0' : 'none'
        }}>
          <div style={{
            width: '100%',
            position: 'relative'
          }}>
            {children}
          </div>

          {/* Universal Help Button - closer to edge on mobile */}
          <div style={{
            position: 'absolute',
            right: isMobile ? '8px' : '40px',
            top: '50%',
            transform: 'translateY(-50%)'
          }}>
            <StandardButton
              variant="icon"
              text="?"
              color="#F9A61B"
              onClick={() => setShowErrorReport(true)}
              style={isMobile ? { width: '40px', height: '40px', fontSize: '16px' } : {}}
            />
          </div>
        </div>

        {/* Error Report Modal */}
        <ErrorReportModal
          isOpen={showErrorReport}
          onClose={() => setShowErrorReport(false)}
          currentView={derivedView}
          currentContext={derivedContext}
        />
      </>
    );
  }

  return (
    <>
      <div style={{
        ...(embedded
          ? { position: 'relative', width: '100%' }
          : { position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }),
        height: '120px',
        background: '#EDF2F8',
        display: 'flex'
      }}>
        {/* Left Section - Branding (480px fixed width) */}
        <div style={{
          width: '480px',
          minWidth: '480px',
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '40px',
          gap: '24px'
        }}>
          {/* Logo - scales smoothly with viewport height, see calculateBrandingScaleRatio above */}
          <img
            src="/images/logos/logo-ournet.png"
            alt="OurNet Logo"
            style={{
              width: `${brandingLogoWidth}px`,
              height: `${brandingLogoHeight}px`,
              transition: 'width 0.2s ease, height 0.2s ease'
            }}
            onError={(e) => {
              // Fallback if image not found
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />

          {/* Fallback logo if image doesn't exist */}
          <div style={{
            display: 'none',
            width: `${brandingLogoWidth}px`,
            height: `${brandingLogoHeight}px`,
            background: '#024B99',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: 'bold',
            color: 'white'
          }}>
            on
          </div>

          {/* Tagline */}
          <div style={{
            fontSize: `${brandingTaglineSize}px`,
            fontWeight: 'bold',
            color: '#024B99',
            letterSpacing: '0.5px',
            transition: 'font-size 0.2s ease'
          }}>
            {t('tagline')}
          </div>
        </div>

        {/* Right Section - Buttons (flexible width) */}
        <div style={{
          flex: 1,
          position: 'relative'
        }}>
          {children}
        </div>

        {/* Universal Help Button - 40px from right edge (hidden when embedded under the
            sidebar; the front page floats its own Help button so this would duplicate it) */}
        {!embedded && (
          <div style={{
            position: 'absolute',
            right: '40px',
            top: '50%',
            transform: 'translateY(-50%)'
          }}>
            <StandardButton
              variant="icon"
              text="?"
              color="#F9A61B"
              title={t('please_help')}
              onClick={() => setShowErrorReport(true)}
            />
          </div>
        )}
      </div>

      {/* Error Report Modal */}
      <ErrorReportModal
        isOpen={showErrorReport}
        onClose={() => setShowErrorReport(false)}
        currentView={derivedView}
        currentContext={derivedContext}
      />
    </>
  );
}

export default FooterContainer;
